/**
 * builderRules — every rule of the assessment builder, pure: the formats a new
 * assessment can start from, how a saved assessment becomes the builder's form
 * and back, what each step needs before the next, and the paper's summary.
 *
 * The builder always edits SECTIONS. A one-part assessment is one section that
 * is saved flat (`isSectioned: false`), exactly the shape every assessment had
 * before sections — so a quick quiz is stored the way it always was, and only
 * a paper that needs sections, coding problems or section timers is sectioned.
 *
 * The server checks again (vc_be controllers/assessmentController.checkSections);
 * these rules exist so a mistake is shown on the field, before anything is sent.
 */

export const STEPS = [
  { key: "format", title: "Format", hint: "Pick a starting point" },
  { key: "details", title: "Details", hint: "Name and instructions" },
  { key: "questions", title: "Questions", hint: "Sections and questions" },
  { key: "rules", title: "Rules", hint: "Time, marks and security" },
  { key: "audience", title: "Students", hint: "Who takes it, and when" },
  { key: "review", title: "Review", hint: "Check and publish" },
];

/**
 * The steps a given session walks through — no format on an edit, pricing for
 * the library. A paper for public exams has no students to choose, and its
 * security and results are set on the public exam, so its rules are only the
 * timer and the marking.
 */
export function stepsFor({ isEdit = false, isLibrary = false, isPublic = false } = {}) {
  return STEPS
    .filter((s) => !(isEdit && s.key === "format"))
    .filter((s) => !(isPublic && !isLibrary && s.key === "audience"))
    .map((s) => {
      if (isLibrary && s.key === "audience") return { ...s, title: "Pricing", hint: "Free or paid on the marketplace" };
      if (isPublic && !isLibrary && s.key === "rules") return { ...s, title: "Time and marks", hint: "Timer and marking" };
      if (isPublic && !isLibrary && s.key === "review") return { ...s, hint: "Check and make it ready" };
      return s;
    });
}

/**
 * Whether the paper is only for public exam links — given to no student, never
 * on anyone's list, and announced to nobody when it is published.
 */
export const isPublicOnly = (form) => form.assignmentType === "public";

/**
 * Switch who the paper is for. Going public makes it an exam (results are the
 * public exam's to decide); coming back starts at batches. What was chosen
 * under each audience is kept, so switching back and forth loses nothing.
 */
export function applyUsage(form, usage) {
  if (usage === "public") {
    if (isPublicOnly(form)) return form;
    return { ...applyType(form, "exam"), assignmentType: "public" };
  }
  if (!isPublicOnly(form)) return form;
  return { ...form, assignmentType: "batch" };
}

export const QUESTION_TYPE_LABELS = {
  single_choice: "Single choice",
  multi_select: "Multiple answers",
  true_false: "True / False",
  text_answer: "Short answer",
  fill_blank: "Fill in the blank",
  numeric_answer: "Numeric",
  image_mcq: "Image choice",
  code_conceptual: "Code reading",
};

export const DIFFICULTY_LABELS = { easy: "Easy", medium: "Medium", hard: "Hard" };

export const LIMITS = { title: 200, sectionTitle: 120, sectionNote: 2000, sectionMinutes: 600, codingMarks: 1000 };

/** "A", "B" … for a section's position. */
export const letterOf = (index) => String.fromCodePoint(65 + (index % 26));

let keySeq = 0;
/** A key for a section that lives only in the builder (React keys, focus). */
export const newKey = () => {
  keySeq += 1;
  return `s${Date.now().toString(36)}${keySeq.toString(36)}`;
};

const idOf = (v) => String(v?._id || v || "");

export const DEFAULT_ANTI_CHEAT = {
  tabSwitchDetection: false,
  maxTabSwitches: 3,
  fullscreenRequired: false,
  disableRightClick: false,
  disableCopyPaste: false,
  autoSubmitOnViolation: false,
};

/**
 * One section. `intent` only decides what the empty section suggests first —
 * every section can hold questions AND coding problems.
 *   source  'pick'   — questions chosen one by one from the bank
 *           'random' — each student gets a fresh draw by `rules`
 */
export function blankSection(patch = {}) {
  return {
    key: newKey(),
    _id: undefined,
    title: "",
    description: "",
    timeLimit: 0,
    source: "pick",
    questions: [],
    rules: [],
    avoidRecentDays: 0,
    coding: [],
    intent: "questions",
    hint: "",
    ...patch,
  };
}

export const blankRule = (patch = {}) => ({ category: "", subcategory: "", difficulty: "", tags: [], count: 10, ...patch });

export function blankForm() {
  return {
    template: "",
    title: "",
    description: "",
    instructions: "",
    // Instructions follow the paper's shape until the author writes their own.
    instructionsTouched: false,
    type: "practice",
    sectioned: false,
    sections: [blankSection()],
    timing: "overall",
    timeLimit: 0,
    deliverCount: 0,
    maxAttempts: 0,
    passingPercentage: 0,
    negativeMarking: false,
    negativeMarkValue: 0,
    partialMarking: false,
    shuffleQuestions: false,
    shuffleOptions: false,
    showResultInstantly: true,
    showExplanation: true,
    showCorrectAnswers: false,
    allowReview: true,
    antiCheat: { ...DEFAULT_ANTI_CHEAT },
    assignmentType: "batch",
    assignedBatches: [],
    assignedCourses: [],
    assignedStudents: [],
    assignmentRules: [],
    excludedStudents: [],
    pricing: { model: "free", period: "", amount: 0, currency: "INR" },
    startDate: "",
    endDate: "",
  };
}

// ── Formats ───────────────────────────────────────────────────────────

export const TEMPLATE_GROUPS = [
  { key: "everyday", title: "Everyday tests" },
  { key: "placement", title: "Placement and hiring" },
  { key: "domain", title: "Domain and competitive exams" },
];

const SECURE = {
  tabSwitchDetection: true,
  maxTabSwitches: 3,
  fullscreenRequired: true,
  disableRightClick: true,
  disableCopyPaste: true,
  autoSubmitOnViolation: false,
};

const EXAM_RESULTS = { showResultInstantly: true, allowReview: false, showCorrectAnswers: false, showExplanation: false };
const PRACTICE_RESULTS = { showResultInstantly: true, allowReview: true, showCorrectAnswers: true, showExplanation: true };

/**
 * A format is a starting point, never a lock-in: it names the sections, sets
 * their timers and the rules, and the author changes any of it afterwards.
 * `sections[].hint` is the sentence an empty section shows.
 */
export const TEMPLATES = [
  {
    key: "quick_quiz",
    group: "everyday",
    title: "Quick quiz",
    blurb: "A short practice check after a class. Instant score and explanations.",
    sections: [{ title: "", minutes: 0, hint: "Add 5–15 questions from your question bank." }],
    timing: "overall",
    timeLimit: 10,
    type: "practice",
    settings: { ...PRACTICE_RESULTS, maxAttempts: 0, passingPercentage: 0, shuffleQuestions: true, shuffleOptions: true },
  },
  {
    key: "class_test",
    group: "everyday",
    title: "Class or unit test",
    blurb: "A marked test for a batch — one attempt, a pass mark, answers shown afterwards.",
    sections: [{ title: "", minutes: 0, hint: "Add the questions for this test from your question bank." }],
    timing: "overall",
    timeLimit: 45,
    type: "exam",
    settings: {
      showResultInstantly: true, allowReview: true, showCorrectAnswers: true, showExplanation: true,
      maxAttempts: 1, passingPercentage: 40, shuffleQuestions: true, shuffleOptions: true,
    },
    antiCheat: { tabSwitchDetection: true, maxTabSwitches: 5 },
  },
  {
    key: "it_placement",
    group: "placement",
    title: "IT placement test",
    badge: "MNC style",
    blurb: "Aptitude & English, then coding, then technical — the format most IT companies use in campus drives.",
    blurbNoCoding: "Aptitude & English, then technical — the format most IT companies use in campus drives.",
    sections: [
      { title: "Aptitude & English", minutes: 30, hint: "Quantitative aptitude, logical reasoning and verbal English. A random draw per student works well here." },
      { title: "Coding", minutes: 45, intent: "coding", hint: "Add 1–3 programming problems from your coding bank. Marks follow the test cases a program passes." },
      { title: "Technical", minutes: 20, hint: "Programming concepts, DBMS, OS, networks — or the stack the company hires for." },
    ],
    timing: "sections",
    type: "exam",
    settings: { ...EXAM_RESULTS, maxAttempts: 1, passingPercentage: 60, shuffleQuestions: true, shuffleOptions: true },
    antiCheat: SECURE,
  },
  {
    key: "nonit_placement",
    group: "placement",
    title: "Non-IT placement test",
    blurb: "For BBA, B.Com, BA and other non-technical roles — aptitude, reasoning, English and role knowledge.",
    sections: [
      { title: "Quantitative aptitude", minutes: 20, hint: "Numbers, percentages, ratios, data interpretation." },
      { title: "Logical reasoning", minutes: 20, hint: "Series, puzzles, arrangements, syllogisms." },
      { title: "Verbal English", minutes: 20, hint: "Grammar, vocabulary and reading comprehension — a passage keeps its questions together." },
      { title: "Role knowledge", minutes: 20, hint: "Sales, accounts, HR, banking operations — whatever the role needs." },
    ],
    timing: "sections",
    type: "exam",
    settings: { ...EXAM_RESULTS, maxAttempts: 1, passingPercentage: 50, shuffleQuestions: true, shuffleOptions: true },
    antiCheat: SECURE,
  },
  {
    key: "medical_coding",
    group: "domain",
    title: "Medical coding assessment",
    blurb: "Terminology and anatomy, ICD-10-CM and CPT/HCPCS coding, and chart-based case studies.",
    sections: [
      { title: "Medical terminology & anatomy", minutes: 0, hint: "Prefixes, suffixes, body systems and abbreviations." },
      { title: "ICD-10-CM coding", minutes: 0, hint: "Diagnosis codes and guidelines. Numeric or short-answer questions take the exact code." },
      { title: "CPT & HCPCS coding", minutes: 0, hint: "Procedure codes, modifiers and HCPCS Level II." },
      { title: "Case studies", minutes: 0, hint: "Attach a patient chart as a passage and ask several questions about it." },
    ],
    timing: "overall",
    timeLimit: 120,
    type: "exam",
    settings: {
      showResultInstantly: true, allowReview: true, showCorrectAnswers: true, showExplanation: true,
      maxAttempts: 1, passingPercentage: 70, shuffleQuestions: false, shuffleOptions: true,
    },
    antiCheat: { tabSwitchDetection: true, maxTabSwitches: 5 },
  },
  {
    key: "competitive",
    group: "domain",
    title: "Banking or government exam",
    blurb: "Timed sections with negative marking — the pattern of bank, SSC and other government exams.",
    sections: [
      { title: "Quantitative aptitude", minutes: 20, hint: "Arithmetic, data interpretation, simplification." },
      { title: "Reasoning ability", minutes: 20, hint: "Puzzles, seating arrangements, coding-decoding." },
      { title: "English language", minutes: 20, hint: "Reading comprehension, error spotting, cloze tests." },
      { title: "General awareness", minutes: 15, hint: "Current affairs, banking and static GK." },
    ],
    timing: "sections",
    type: "exam",
    settings: {
      ...EXAM_RESULTS, maxAttempts: 1, passingPercentage: 0, shuffleQuestions: true, shuffleOptions: true,
      negativeMarking: true, negativeMarkValue: 0.25,
    },
    antiCheat: SECURE,
  },
  {
    key: "scratch",
    group: "everyday",
    title: "Start from scratch",
    blurb: "One blank part and the default rules. Add sections whenever you need them.",
    sections: [{ title: "", minutes: 0, hint: "" }],
    timing: "overall",
    timeLimit: 0,
    type: "practice",
    settings: { ...PRACTICE_RESULTS },
  },
];

/**
 * The formats as this institute sees them. Where Coding Practice is off, a
 * format's coding section is left out and its description says nothing about
 * coding — the IT placement test becomes aptitude, then technical.
 */
export function templatesFor({ coding = true } = {}) {
  if (coding) return TEMPLATES;
  return TEMPLATES.map((t) => (t.sections.some((s) => s.intent === "coding")
    ? { ...t, blurb: t.blurbNoCoding || t.blurb, sections: t.sections.filter((s) => s.intent !== "coding") }
    : t));
}

export const templateByKey = (key, opts) => templatesFor(opts).find((t) => t.key === key) || null;

/**
 * The form with a format applied. Keeps what the author already wrote — the
 * title, the description, who it is for — and replaces the paper's shape.
 * `opts.coding: false` applies the format without its coding section.
 */
export function applyTemplate(form, key, opts) {
  const t = templateByKey(key, opts);
  if (!t) return form;
  const sectioned = t.sections.length > 1;
  const next = {
    ...form,
    template: t.key,
    type: t.type,
    sectioned,
    sections: t.sections.map((s, i) => blankSection({
      title: s.title || (sectioned ? `Section ${letterOf(i)}` : ""),
      timeLimit: s.minutes || 0,
      intent: s.intent || "questions",
      hint: s.hint || "",
    })),
    timing: sectioned ? t.timing : "overall",
    timeLimit: t.timing === "sections" ? 0 : (t.timeLimit || 0),
    deliverCount: 0,
    negativeMarking: false,
    negativeMarkValue: 0,
    partialMarking: false,
    ...t.settings,
    antiCheat: { ...DEFAULT_ANTI_CHEAT, ...(t.antiCheat || {}) },
  };
  if (!next.instructionsTouched) next.instructions = defaultInstructions(next);
  return next;
}

/** Practice and exam set sensible result rules; the author can change each. */
export function applyType(form, type) {
  const results = type === "exam"
    ? { showResultInstantly: true, allowReview: true, showCorrectAnswers: false, showExplanation: false }
    : PRACTICE_RESULTS;
  return { ...form, type, ...results };
}

// ── The paper ─────────────────────────────────────────────────────────

const ruleCount = (rules) => (rules || []).reduce((sum, r) => sum + (Number(r.count) || 0), 0);
const questionMarks = (q) => Number(q.overrideMarks ?? q.marks ?? 1) || 0;

/** How many items each section serves, and what they are worth. */
/** Timed sections in order need at least two sections to be in order. */
export const timedSections = (form) =>
  !!form.sectioned && form.timing === "sections" && (form.sections || []).length > 1;

export function paperSummary(form) {
  const timed = timedSections(form);
  const sections = (form.sections || []).map((s, i) => {
    const random = s.source === "random";
    const questionCount = random ? ruleCount(s.rules) : (s.questions || []).length;
    const codingCount = (s.coding || []).length;
    const codingMarks = (s.coding || []).reduce((sum, c) => sum + (Number(c.marks) || 0), 0);
    const pickedMarks = random ? null : (s.questions || []).reduce((sum, q) => sum + questionMarks(q), 0);
    return {
      key: s.key,
      letter: letterOf(i),
      title: s.title || (form.sectioned ? `Section ${letterOf(i)}` : "Questions"),
      random,
      questionCount,
      codingCount,
      marks: random ? null : pickedMarks + codingMarks,
      // Marks of a random draw depend on what is drawn; coding marks are known.
      codingMarks,
      minutes: timed ? (Number(s.timeLimit) || 0) : null,
      empty: questionCount + codingCount === 0,
    };
  });
  const pooled = !form.sectioned && form.sections?.[0]?.source === "pick" && form.deliverCount > 0
    ? Math.min(form.deliverCount, sections[0]?.questionCount || 0)
    : null;
  const questions = pooled ?? sections.reduce((sum, s) => sum + s.questionCount, 0);
  const coding = sections.reduce((sum, s) => sum + s.codingCount, 0);
  const marksKnown = sections.every((s) => s.marks !== null) && pooled === null;
  return {
    sections,
    totals: {
      questions,
      coding,
      marks: marksKnown ? sections.reduce((sum, s) => sum + s.marks, 0) : null,
      minutes: timed ? sections.reduce((sum, s) => sum + (s.minutes || 0), 0) : (Number(form.timeLimit) || 0),
      timedSections: timed,
      pooled,
    },
  };
}

/** Whether the paper must be saved sectioned. */
export const needsSections = (form) =>
  form.sectioned
  || (form.sections || []).some((s) => (s.coding || []).length > 0)
  || (form.sections || []).length > 1;

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Instructions written from the paper's shape — the builder's suggestion. */
export function defaultInstructions(form) {
  const { sections, totals } = paperSummary(form);
  const lines = [];
  const named = needsSections(form);
  if (named && sections.length > 1) {
    if (totals.timedSections) {
      lines.push(`This assessment has ${sections.length} sections, answered in this order:`);
      sections.forEach((s) => lines.push(`  ${s.letter}. ${s.title} — ${plural(s.minutes || 0, "minute")}`));
      lines.push("Each section has its own timer. When a section's time ends, the next one starts — you cannot go back to a finished section.");
    } else {
      lines.push(`This assessment has ${sections.length} sections: ${sections.map((s) => s.title).join(", ")}.`);
      lines.push("You can move between sections at any time.");
    }
  }
  if (!totals.timedSections) {
    lines.push(totals.minutes > 0
      ? `You have ${plural(totals.minutes, "minute")} in total. The exam is submitted automatically when time runs out.`
      : "There is no time limit.");
  }
  if (totals.coding > 0) {
    lines.push("For a coding problem, write your program in one of the languages offered. \"Run code\" checks it against the sample tests; when you submit, it is checked against hidden tests too, and marks follow the tests it passes.");
  }
  if (form.negativeMarking && Number(form.negativeMarkValue) > 0) {
    lines.push(`A wrong answer loses ${form.negativeMarkValue} ${Number(form.negativeMarkValue) === 1 ? "mark" : "marks"}. An unanswered question loses nothing.`);
  }
  if (form.antiCheat?.fullscreenRequired) lines.push("The exam runs in full screen. Leaving it is recorded.");
  if (form.antiCheat?.tabSwitchDetection) lines.push("Switching to another tab or window is recorded.");
  lines.push("Your answers are saved as you go. Use \"Mark for review\" to come back to a question.");
  return lines.join("\n");
}

// ── Loading a saved assessment ────────────────────────────────────────

const questionRow = (entry) => {
  const q = entry?.question && typeof entry.question === "object" ? entry.question : null;
  return {
    question: idOf(entry?.question),
    questionText: q?.questionText || entry?.questionText || "Question",
    questionType: q?.questionType || entry?.questionType || "",
    difficulty: q?.difficulty || entry?.difficulty || "",
    marks: q?.marks ?? entry?.marks ?? 1,
    passage: q?.passage ? idOf(q.passage) : null,
    overrideMarks: entry?.overrideMarks ?? null,
  };
};

const codingRow = (entry) => {
  const p = entry?.question && typeof entry.question === "object" ? entry.question : null;
  const tests = (p?.testCases || []).filter((t) => String(t?.expectedOutput ?? "").trim() !== "");
  return {
    question: idOf(entry?.question),
    title: p?.title || "Coding problem",
    difficulty: String(p?.difficulty || "").toLowerCase(),
    languages: (p?.categories || []).map((c) => c?.name).filter(Boolean),
    sampleCount: tests.filter((t) => !t.isHidden).length,
    hiddenCount: tests.filter((t) => t.isHidden).length,
    marks: entry?.marks ?? 10,
    missing: !p,
  };
};

const ruleRow = (r) => ({
  category: idOf(r?.category),
  subcategory: idOf(r?.subcategory),
  difficulty: r?.difficulty || "",
  tags: r?.tags || [],
  count: Number(r?.count) || 1,
});

const toLocalInput = (value, toDateTimeLocal) => (value ? toDateTimeLocal(value) : "");

/** A saved assessment (GET /assessments/:id, populated) as the builder's form. */
export function fromAssessment(a, { toDateTimeLocal = (v) => v } = {}) {
  const form = blankForm();
  const sectioned = !!a.isSectioned && (a.sections || []).length > 0;
  const sections = sectioned
    ? [...a.sections].sort((x, y) => (x.order || 0) - (y.order || 0)).map((s) => {
      const random = (s.dynamicConfig?.rules || []).length > 0;
      return blankSection({
        _id: s._id,
        title: s.title || "",
        description: s.description || "",
        timeLimit: Number(s.timeLimit) || 0,
        source: random ? "random" : "pick",
        questions: random ? [] : (s.questions || []).map(questionRow),
        rules: random ? s.dynamicConfig.rules.map(ruleRow) : [],
        avoidRecentDays: s.dynamicConfig?.avoidRecentDays || 0,
        coding: (s.codingQuestions || []).map(codingRow),
        intent: (s.codingQuestions || []).length && !(s.questions || []).length && !random ? "coding" : "questions",
      });
    })
    : [blankSection({
      source: a.selectionMode === "dynamic" ? "random" : "pick",
      questions: a.selectionMode === "dynamic" ? [] : (a.questions || []).map(questionRow),
      rules: a.selectionMode === "dynamic" ? (a.dynamicConfig?.rules || []).map(ruleRow) : [],
      avoidRecentDays: a.dynamicConfig?.avoidRecentDays || 0,
    })];

  const type = a.mode === "practice" || a.mode === "exam"
    ? a.mode
    : (a.allowReview !== false && a.showExplanation !== false ? "practice" : "exam");

  return {
    ...form,
    template: "",
    title: a.title || "",
    description: a.description || "",
    instructions: a.instructions || "",
    instructionsTouched: true,
    type,
    sectioned,
    sections,
    timing: sectioned && a.sectionNavigation === "sequential" ? "sections" : "overall",
    timeLimit: Number(a.timeLimit) || 0,
    deliverCount: Number(a.deliverCount) || 0,
    maxAttempts: Number(a.maxAttempts) || 0,
    passingPercentage: Number(a.passingPercentage) || 0,
    negativeMarking: !!a.negativeMarking,
    negativeMarkValue: Number(a.negativeMarkValue) || 0,
    partialMarking: !!a.partialMarking,
    shuffleQuestions: !!a.shuffleQuestions,
    shuffleOptions: !!a.shuffleOptions,
    showResultInstantly: a.showResultInstantly !== false,
    showExplanation: a.showExplanation !== false,
    showCorrectAnswers: !!a.showCorrectAnswers,
    allowReview: a.allowReview !== false,
    antiCheat: { ...DEFAULT_ANTI_CHEAT, ...(a.antiCheat || {}) },
    assignmentType: a.assignmentType || "batch",
    assignedBatches: (a.assignedBatches || []).map(idOf),
    assignedCourses: (a.assignedCourses || []).map(idOf),
    assignedStudents: (a.assignedStudents || []).map(idOf),
    assignmentRules: (a.assignmentRules || []).map((r) => ({
      targetType: r.targetType || "batch",
      target: idOf(r.target),
      startDate: toLocalInput(r.startDate, toDateTimeLocal),
      endDate: toLocalInput(r.endDate, toDateTimeLocal),
      maxAttempts: r.maxAttempts == null ? "" : String(r.maxAttempts),
      passingPercentage: r.passingPercentage == null ? "" : String(r.passingPercentage),
    })),
    excludedStudents: (a.excludedStudents || []).map(idOf),
    pricing: {
      model: a.pricing?.model || "free",
      period: a.pricing?.period || "",
      amount: Number(a.pricing?.amount) || 0,
      currency: a.pricing?.currency || "INR",
    },
    startDate: toLocalInput(a.startDate, toDateTimeLocal),
    endDate: toLocalInput(a.endDate, toDateTimeLocal),
  };
}

// ── Saving ────────────────────────────────────────────────────────────

const cleanRules = (rules) => (rules || [])
  .filter((r) => Number(r.count) > 0)
  .map((r) => ({
    category: r.category || null,
    subcategory: r.subcategory || null,
    difficulty: r.difficulty || "",
    tags: r.tags || [],
    count: Math.max(1, Number.parseInt(r.count, 10) || 1),
  }));

const NO_STUDENTS = {
  assignedBatches: [], assignedCourses: [], assignedStudents: [], assignmentRules: [], excludedStudents: [],
};

/** Who the paper is given to, as saved. Only the audience showing is kept. */
function audienceOf(form, fromDateTimeLocal) {
  if (isPublicOnly(form)) return { assignmentType: "public", ...NO_STUDENTS };
  return {
    assignmentType: form.assignmentType,
    assignedBatches: form.assignmentType === "batch" ? form.assignedBatches : [],
    assignedCourses: form.assignmentType === "course" ? form.assignedCourses : [],
    assignedStudents: form.assignmentType === "direct" ? form.assignedStudents : [],
    assignmentRules: form.assignmentType === "rules"
      ? form.assignmentRules.filter((r) => r.target).map((r) => ({
        targetType: r.targetType,
        target: r.target,
        startDate: fromDateTimeLocal(r.startDate),
        endDate: fromDateTimeLocal(r.endDate),
        maxAttempts: r.maxAttempts === "" ? null : Number(r.maxAttempts),
        passingPercentage: r.passingPercentage === "" ? null : Number(r.passingPercentage),
      }))
      : [],
    excludedStudents: form.excludedStudents,
  };
}

/**
 * The body POST/PUT /assessments takes. A one-part paper without coding
 * problems is saved flat; anything else sectioned.
 */
export function toPayload(form, { isLibrary = false, fromDateTimeLocal = (v) => v || null } = {}) {
  const sectioned = needsSections(form);
  const timed = sectioned && timedSections(form);
  const first = form.sections?.[0] || blankSection();
  const instructions = form.instructionsTouched ? form.instructions : defaultInstructions(form);

  const base = {
    title: form.title.trim(),
    description: form.description || "",
    instructions: instructions || "",
    mode: sectioned ? "sectioned_exam" : (form.type === "exam" ? "exam" : "practice"),
    timeLimit: timed
      ? form.sections.reduce((sum, s) => sum + (Number(s.timeLimit) || 0), 0)
      : Math.max(0, Number.parseInt(form.timeLimit, 10) || 0),
    maxAttempts: Math.max(0, Number.parseInt(form.maxAttempts, 10) || 0),
    passingPercentage: Math.min(100, Math.max(0, Number(form.passingPercentage) || 0)),
    negativeMarking: !!form.negativeMarking,
    negativeMarkValue: form.negativeMarking ? Math.max(0, Number(form.negativeMarkValue) || 0) : 0,
    partialMarking: !!form.partialMarking,
    shuffleQuestions: !!form.shuffleQuestions,
    shuffleOptions: !!form.shuffleOptions,
    showResultInstantly: form.showResultInstantly !== false,
    showExplanation: !!form.showExplanation,
    showCorrectAnswers: !!form.allowReview && !!form.showCorrectAnswers,
    allowReview: !!form.allowReview,
    antiCheat: { ...DEFAULT_ANTI_CHEAT, ...form.antiCheat },
  };

  const paper = sectioned
    ? {
      isSectioned: true,
      sectionNavigation: timed ? "sequential" : "free",
      selectionMode: "manual",
      questions: [],
      dynamicConfig: null,
      deliverCount: 0,
      sections: form.sections.map((s, i) => {
        const random = s.source === "random";
        return {
          ...(s._id ? { _id: s._id } : {}),
          title: (s.title || "").trim() || (form.sections.length > 1 ? `Section ${letterOf(i)}` : "Questions"),
          description: s.description || "",
          timeLimit: timed ? Math.max(0, Number.parseInt(s.timeLimit, 10) || 0) : 0,
          order: i,
          questions: random ? [] : (s.questions || []).map((q, j) => ({
            question: q.question, order: j, overrideMarks: q.overrideMarks ?? null,
          })),
          dynamicConfig: random
            ? { rules: cleanRules(s.rules), totalQuestions: ruleCount(cleanRules(s.rules)), avoidRecentDays: s.avoidRecentDays || 0 }
            : null,
          codingQuestions: (s.coding || []).map((c, j) => ({ question: c.question, marks: Number(c.marks) || 10, order: j })),
        };
      }),
    }
    : first.source === "random"
      ? {
        isSectioned: false,
        sections: [],
        selectionMode: "dynamic",
        questions: [],
        dynamicConfig: { rules: cleanRules(first.rules), totalQuestions: ruleCount(cleanRules(first.rules)), avoidRecentDays: first.avoidRecentDays || 0 },
        deliverCount: 0,
      }
      : {
        isSectioned: false,
        sections: [],
        selectionMode: "manual",
        questions: (first.questions || []).map((q, j) => ({ question: q.question, order: j, overrideMarks: q.overrideMarks ?? null })),
        dynamicConfig: null,
        deliverCount: form.deliverCount > 0 && form.deliverCount < (first.questions || []).length
          ? Number.parseInt(form.deliverCount, 10)
          : 0,
      };

  const audience = isLibrary ? { pricing: form.pricing } : audienceOf(form, fromDateTimeLocal);

  // A public exam keeps its own schedule, so the paper carries no dates.
  const publicOnly = !isLibrary && isPublicOnly(form);
  return {
    ...base,
    ...paper,
    ...audience,
    startDate: publicOnly ? null : fromDateTimeLocal(form.startDate),
    endDate: publicOnly ? null : fromDateTimeLocal(form.endDate),
  };
}

// ── Checks ────────────────────────────────────────────────────────────

const sectionName = (form, i) => {
  const s = form.sections[i];
  return s?.title?.trim() || (form.sectioned ? `Section ${letterOf(i)}` : "the questions");
};

/**
 * What stops a step from moving on — each a sentence, with the section it is
 * about. `ctx.codingAllowed` is the server's answer for this institute.
 */
export function stepProblems(form, step, ctx = {}) {
  const out = [];
  if (step === "details") {
    if (!form.title.trim()) out.push({ field: "title", message: "Give the assessment a name." });
    else if (form.title.trim().length > LIMITS.title) out.push({ field: "title", message: `Keep the name under ${LIMITS.title} characters.` });
  }
  if (step === "questions") {
    const { totals } = paperSummary(form);
    if (totals.questions + totals.coding === 0) {
      out.push({ message: "Add at least one question before you continue." });
    }
    form.sections.forEach((s, i) => {
      if (s.source === "random" && (s.rules || []).some((r) => !(Number(r.count) > 0))) {
        out.push({ section: s.key, message: `Each random rule in ${sectionName(form, i)} needs a count of at least 1.` });
      }
      if ((s.coding || []).some((c) => !(Number(c.marks) > 0) || Number(c.marks) > LIMITS.codingMarks)) {
        out.push({ section: s.key, message: `Give each coding problem in ${sectionName(form, i)} between 1 and ${LIMITS.codingMarks} marks.` });
      }
      if ((s.coding || []).length && ctx.codingAllowed === false) {
        out.push({ section: s.key, message: `${sectionName(form, i)} has coding problems, which are not available here.` });
      }
    });
  }
  if (step === "rules") {
    if (timedSections(form)) {
      form.sections.forEach((s, i) => {
        const m = Number(s.timeLimit);
        if (!(m > 0)) out.push({ section: s.key, field: `time-${s.key}`, message: `Give ${sectionName(form, i)} a time in minutes.` });
        else if (m > LIMITS.sectionMinutes) out.push({ section: s.key, field: `time-${s.key}`, message: `Keep ${sectionName(form, i)} under ${LIMITS.sectionMinutes} minutes.` });
      });
    }
    const p = Number(form.passingPercentage);
    if (!(p >= 0 && p <= 100)) out.push({ field: "passingPercentage", message: "The pass mark is a percentage from 0 to 100." });
    if (form.negativeMarking && !(Number(form.negativeMarkValue) > 0)) {
      out.push({ field: "negativeMarkValue", message: "Say how many marks a wrong answer loses." });
    }
    if (Number(form.maxAttempts) < 0) out.push({ field: "maxAttempts", message: "Attempts cannot be negative." });
  }
  return out;
}

/**
 * Everything that must be true before students can be given the paper. Each
 * names the step that fixes it, for the review screen's "Fix" link.
 */
export function publishProblems(form, ctx = {}) {
  const out = [];
  for (const step of ["details", "questions", "rules"]) {
    for (const p of stepProblems(form, step, ctx)) out.push({ ...p, step });
  }
  const { sections } = paperSummary(form);
  if (needsSections(form)) {
    sections.forEach((s, i) => {
      if (s.empty) out.push({ step: "questions", section: s.key, message: `${sectionName(form, i)} is empty — add questions or remove it.` });
    });
  }
  if (!ctx.isLibrary && !isPublicOnly(form)) {
    const missing = {
      batch: form.assignedBatches.length === 0 && "Choose at least one batch.",
      course: form.assignedCourses.length === 0 && "Choose at least one course.",
      direct: form.assignedStudents.length === 0 && "Choose at least one student.",
      rules: !form.assignmentRules.some((r) => r.target) && "Add at least one rule with a batch or course.",
    }[form.assignmentType];
    if (missing) out.push({ step: "audience", message: missing });
    if (form.startDate && form.endDate && new Date(form.endDate) <= new Date(form.startDate)) {
      out.push({ step: "audience", message: "The closing time must be after the opening time." });
    }
  }
  return out;
}

/** Worth saying before publishing, but not stopping anyone. */
export function publishWarnings(form) {
  const out = [];
  const { totals, sections } = paperSummary(form);
  if (form.type === "exam" && !totals.timedSections && !(totals.minutes > 0)) {
    out.push("This exam has no time limit.");
  }
  sections.forEach((s, i) => {
    const sec = form.sections[i];
    if (s.random) {
      (sec.rules || []).forEach((r) => {
        if (r.available != null && Number(r.count) > r.available) {
          out.push(`${s.title}: a rule asks for ${r.count} questions but only ${r.available} match — students will get ${r.available}.`);
        }
      });
    }
  });
  if (form.antiCheat?.disableCopyPaste && totals.coding > 0) {
    out.push("Copy and paste is blocked, so students cannot paste into the code editor either.");
  }
  return out;
}

/** The question ids already on the paper, and which section holds each. */
export function questionsOnPaper(form) {
  const map = new Map();
  form.sections.forEach((s, i) => {
    for (const q of s.questions || []) map.set(String(q.question), { key: s.key, name: sectionName(form, i) });
  });
  return map;
}

export function codingOnPaper(form) {
  const map = new Map();
  form.sections.forEach((s, i) => {
    for (const c of s.coding || []) map.set(String(c.question), { key: s.key, name: sectionName(form, i) });
  });
  return map;
}
